import { createHoldToken } from 'api/holdToken';
import { useEffect, useState, useCallback, useRef } from 'react';

function getStoredToken() {
  const raw = localStorage.getItem('currentHoldToken');
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function useHoldToken(onTokenUpdate?: () => void) {
  const [holdToken, setHoldToken] = useState<string | null>(null);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isFetchingRef = useRef<boolean>(false);

  const onTokenUpdateRef = useRef(onTokenUpdate);

  useEffect(() => {
    onTokenUpdateRef.current = onTokenUpdate;
  }, [onTokenUpdate]);

  const clearScheduledRefresh = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const fetchNewToken = useCallback(async () => {
    if (isFetchingRef.current) {
      return null;
    }

    isFetchingRef.current = true;

    try {
      const result = await createHoldToken();

      localStorage.setItem('currentHoldToken', JSON.stringify(result));
      setHoldToken(result.holdToken);

      const expiry = new Date(result.expiresAt).getTime();
      const now = Date.now();
      const refreshAt = expiry - now - 60_000;

      clearScheduledRefresh();

      if (refreshAt > 0) {
        timeoutRef.current = setTimeout(() => {
          fetchNewToken();
        }, refreshAt);
      }

      if (onTokenUpdateRef.current) {
        onTokenUpdateRef.current();
      }

      return result.holdToken;
    } catch (error) {
      console.error('Error creating hold token:', error);
      return null;
    } finally {
      isFetchingRef.current = false;
    }
  }, [clearScheduledRefresh]);

  useEffect(() => {
    const saved = getStoredToken();

    if (saved && new Date(saved.expiresAt) > new Date()) {
      setHoldToken(saved.holdToken);
      const expiry = new Date(saved.expiresAt).getTime();
      const now = Date.now();
      const refreshAt = expiry - now - 60_000;

      if (refreshAt > 0) {
        timeoutRef.current = setTimeout(() => {
          fetchNewToken();
        }, refreshAt);
      }
    } else {
      fetchNewToken();
    }

    return () => {
      clearScheduledRefresh();
    };
  }, [clearScheduledRefresh, fetchNewToken]);
  return holdToken;
}
